import React, { useState, useEffect } from "react";
import { PortableText } from "@portabletext/react";
import Image from "components/image";
import Link from "helpers/link";
import { ReactComponent as Checkmark } from "assets/svgs/checkmark.svg";
import { ReactComponent as WhatsappIcon } from "assets/svgs/whatsapp.svg";
import useEmailSignup from "hooks/useEmailSignup";
import Button from "components/button";
import clsx from "clsx/lite";
import { getRgba } from "helpers/get-rgba";
import { formatTitle } from "helpers/format-title";
import ReviewsSnippet from "components/reviews-snippet";
import TypeFormSurvey from "components/typeform-survey";
import SmsButton from "../sms-button";


const TableSerializer = ({ value: node }) => {
	const headers = node.rows[0].cells.map((header, i) => (
		<th className="border font-body" key={i}>
			{header}
		</th>
	));

	const tableRows = node.rows.slice(1).map((tr, i) => (
		<tr key={i}>
			{tr.cells.map((cell, i) => {
				return (
					<td className="border p-3" key={i}>
						{cell}
					</td>
				);
			})}
		</tr>
	));

	return (
		<table className="border my-5">
			<thead>
				<tr className="border">{headers}</tr>
			</thead>
			<tbody>{tableRows}</tbody>
		</table>
	);
};

const Video = ({ value }) => {
	const { controls, autoplay, muted, loop, video } = value;
	return (
		<video className="w-full py-4" controls={controls} autoPlay={autoplay} muted={muted} loop={loop}>
			<source src={video} type="video/mp4" />
			Your browser does not support HTML5 video.
		</video>
	);
};

const NutrientsTable = ({ value: node }) => {
	const headers = node.rows[0].cells.map((header, i) => (
		<th className="font-body" key={i}>
			{header}
		</th>
	));

	const tableRows = node.rows.slice(1).map((tr, i) => (
		<tr key={i}>
			{tr.cells.map((cell, i) => {
				return (
					<td className="py-2" key={i}>
						{cell}
					</td>
				);
			})}
		</tr>
	));

	return (
		<table className="my-5 text-description w-full text-left">
			<thead className="text-left">
				<tr>{headers}</tr>
			</thead>
			<tbody>{tableRows}</tbody>
		</table>
	);
};

const IngredientsTable = ({ value: node }) => {
	const headers = node.rows[0].cells.map((header, i) => (
		<th className="font-body border-b border-gray-300" key={i}>
			{header}
		</th>
	));

	const tableRows = node.rows.slice(1).map((tr, i) => (
		<tr key={i} className="!border-bottom !border-gray-500">
			{tr.cells.map((cell, i) => {
				return (
					<td className="py-2 md:py-3 border-b border-gray-300" key={i}>
						{cell}
					</td>
				);
			})}
		</tr>
	));

	return (
		<table className="text-left my-5">
			<thead className="text-left">
				<tr>{headers}</tr>
			</thead>
			<tbody>{tableRows}</tbody>
		</table>
	);
};

const ListWithTicks = ({ value: node }) => (
	<div className="flex flex-col w-full md:my-5 my-3">
		<h2 className={`mb-3 ${node?.centerText ? "text-center md:text-left" : "text-left"}`}>{formatTitle(node?.title)}</h2>
		<p className="mb-3">{node?.subtitle}</p>
		{node?.list.map((l) => (
			<div key={l._key} className="my-3 flex flex-row items-center">
				<Checkmark className="w-5 flex-shrink-0 mr-5" />
				<div className="text-base">
					<strong>{l.title}</strong>
					<p className="text-secondary" dangerouslySetInnerHTML={{ __html: l.text }}></p>
				</div>
			</div>
		))}
	</div>
);

const EmailSignup = ({ value }) => {
	const { listId, btnText, placeholder = "", successMessage = "" } = value;
	const { success, email, error, handleSubscribe, onEmailChange } = useEmailSignup(listId);
	return (
		<div>
			{success ? (
				<div className="lg:max-w-xl">{successMessage && <p>{successMessage}</p>}</div>
			) : (
				<form onSubmit={handleSubscribe} className="flex flex-row box-border overflow-visible items-stretch max-h-14">
					<div className="flex flex-grow flex-col self-end">
						<input
							type="email"
							value={email}
							onChange={onEmailChange}
							className={`email-signup-input ${error ? "border-[#D0331F] focus:border-[#D0331F]" : ""}`}
							placeholder={`${placeholder ? `${placeholder}...` : "Email..."}`}
						/>
						{error && (
							<div className="relative w-full">
								<div className="absolute z-[1] right-0 rounded top-2.5">
									<div className="hover-error">
										<span>{error}</span>
									</div>
								</div>
							</div>
						)}
					</div>
					<button type="submit" className={`email-signup-button whitespace-nowrap px-2 w-auto ${email !== "" ? "bg-black" : ""}`}>
						{btnText}
					</button>
				</form>
			)}
		</div>
	);
};


const WhatsappBtn = ({ value }) => (
	<a
		className="flex border w-fit px-3 py-2 justify-between items-end hover:bg-gray-50 transition-colors"
		href={`https://api.whatsapp.com/send/?phone=${value.number}&text=${encodeURI(value.text)}`}
		target="_blank"
		rel="noreferrer"
	>
		<WhatsappIcon className="mr-2" />
		{value.btnText}
	</a>
);

const SmsBtn = ({ value }) => (
	<SmsButton btnText={value.btnText} formUrl={value.formUrl}/>
);

const KlaviyoForm = ({ value }) => {
	const [initialRender, setInitialRender] = useState(false);
	useEffect(() => {
		setInitialRender(true);
	}, []);

	if (initialRender) {
		return <div className={`klaviyo-form-${value?.id}`} />;
	}
};

const TypeformSurvey = ({ value }) => <TypeFormSurvey id={value?.id} />;

//Types

const ImageRenderer = ({ value: node }) => <Image image={node} alt={node.alt} to={node.url} />;

const ButtonRenderer = ({ value }) => <Button data={value} />;
// Marks

const Links = ({ children, value }) => (
	<Link className="underline" to={value.href}>
		{children}
	</Link>
);

const Color = ({ children, value }) => <span className="text-unset font-unset" style={{ color: getRgba(value?.color) }}>{children}</span>;

const Steps = ({ value }) => {
	const { title, steps } = value;
	return (
		<div className="mb-4">
			<h4 className="mb-1 text-description text-sm font-normal tracking-widest">{formatTitle(title)}</h4>

			{steps?.map((s, i) => {
				switch (s._type) {
					case "content":
						return (
							<div className="flex items-center my-3" key={s._key}>
								<div className="w-[10%] text-description flex-shrink-0">
									<span className="text-small inline-flex justify-center items-center text-center rounded-circle bg-white border-[#ececec] border-[1px] w-[22px] h-[22px]">
										{i + 1}
									</span>
								</div>
								<span className=" text-description">{s?.content} </span>
							</div>
						);

					case "video":
						return (
							<div key={s._key} className="py-2">
								<video className="w-full" controls={s.controls} autoPlay={s.autoplay} muted={s.muted} loop={s.loop}>
									<source src={s?.video} type="video/mp4" />
									Your browser does not support HTML5 video.
								</video>
							</div>
						);

					default:
						return <span key={s._key}>Cannot render, try refreshing the page </span>;
				}
			})}
		</div>
	);
};

const EmbedHTML = ({ value }) => <div dangerouslySetInnerHTML={{ __html: value?.html }} />;

const Reviews = ({ value }) => <ReviewsSnippet type={value.reviews_type} />;

const richTextComponents = {
	types: {
		table: TableSerializer,
		nutrientsTable: NutrientsTable,
		ingredientsTable: IngredientsTable,
		Image: ImageRenderer,
		Button: ButtonRenderer,
		Steps: Steps,
		Video: Video,
		listWithTicks: ListWithTicks,
		emailSignup: EmailSignup,
		whatsappBtn: WhatsappBtn,
		smsBtn: SmsBtn,
		klaviyoForm: KlaviyoForm,
		typeformSurvey: TypeformSurvey,
		embedHTML: EmbedHTML,
		reviews: Reviews,
	},
	block: { avenir18: ({ children }) => <p className="text-base-lg">{children}</p> },

	list: {
		checkmark: ({ children }) => (
			<ul className="list-none">
				{React.Children.toArray(children).map((child, index) => (
					<li key={index} className="flex items-center my-1.5">
						<Checkmark className="mr-5 size-3.5" />
						{child.props ? <span>{child.props.children}</span> : child}
					</li>
				))}
			</ul>
		),
		normal: ({ children }) => <ul>{children}</ul>,
	},
	marks: {
		link: Links,
		highlight: Color,
		color: Color,
	},
};

const RichText = ({ data = {}, value = {}, isFullWidth = false, className = "" }) => {
	const alignClass = data?.center ? "text-center" : data?.leftTable ? "md:px-10 lg:px-20 p-5 my-5" : "";
	const mobileTextAlign = value?.mobileCenterAlignment ? "mobile-center" : "";
	const desktopTextAlign = value?.desktopCenterAlignment ? "desktop-center" : "";

	return (
		<div className={clsx(!isFullWidth && "hd-screen-max-width", alignClass, mobileTextAlign, desktopTextAlign, className)}>
			<PortableText value={value?.data || data?.data || value} components={richTextComponents} />
		</div>
	);
};

export default RichText;
